/**
 * UploadProgressDock
 * ------------------
 * Persistent floating widget that shows the current background upload's
 * progress on every page so the user knows it's still going. Hidden when
 * idle. When the upload finishes successfully it auto-hides after a beat.
 * For a pasted YouTube link the "upload" is our server downloading the
 * video, so that phase is labelled as a YouTube download.
 */
import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import CloseIcon from "@mui/icons-material/Close";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import { useUpload } from "../contexts/UploadContext";

// What the server is doing while it pulls a YouTube video into storage.
const YOUTUBE_STAGE_LABELS = {
  queued: "Waiting for a free download slot…",
  connecting: "Connecting to YouTube…",
  waiting: "Starting download from YouTube…",
  downloading: "Downloading from YouTube…",
  finalizing: "Saving your video…",
};

function formatSpeed(bytesPerSecond) {
  if (!bytesPerSecond) return null;
  const mb = bytesPerSecond / 1e6;
  return mb >= 1 ? `${mb.toFixed(1)} MB/s` : `${Math.round(bytesPerSecond / 1e3)} KB/s`;
}

/** "3 min 20 sec" style remaining-time label, or null when unknown. */
function formatEta(seconds) {
  if (seconds == null || seconds < 0) return null;
  if (seconds < 60) return `~${Math.max(1, Math.round(seconds))} sec left`;
  const mins = Math.round(seconds / 60);
  if (mins < 60) return `~${mins} min left`;
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return `~${h} h ${m} m left`;
}

export default function UploadProgressDock() {
  const {
    status,
    source,
    stage,
    percent,
    etaSeconds,
    speedBps,
    thumbnailUrl,
    video_id,
    error,
    fileName,
    dismiss,
  } = useUpload();
  const navigate = useNavigate();

  // Auto-dismiss successful uploads after 6 seconds
  useEffect(() => {
    if (status !== "success") return;
    const t = setTimeout(() => dismiss(), 6000);
    return () => clearTimeout(t);
  }, [status, dismiss]);

  if (status === "idle") return null;

  const isDone = status === "success" || status === "error";
  const isYouTubeDownload = status === "uploading" && source === "youtube";
  const headline = isYouTubeDownload
    ? YOUTUBE_STAGE_LABELS[stage] || "Downloading from YouTube…"
    : status === "uploading"
      ? "Uploading video…"
      : status === "processing"
        ? "Processing video…"
        : status === "success"
          ? "Done — ready to view"
          : "Upload failed";

  const etaLabel =
    status === "processing" || isYouTubeDownload ? formatEta(etaSeconds) : null;
  // Sub-line: error first, then live download stats / ETA, else the filename.
  const subline =
    error ||
    (isYouTubeDownload && stage === "downloading"
      ? [`${Math.round(percent)}%`, formatSpeed(speedBps), etaLabel]
          .filter(Boolean)
          .join(" • ")
      : etaLabel
        ? `${Math.round(percent)}% • ${etaLabel}`
        : status === "processing"
          ? "Working in the background — you can leave this page"
          : fileName || "Working in the background");

  return (
    <div
      role="status"
      aria-live="polite"
      className="fixed z-50 bottom-4 right-4 left-4 sm:left-auto sm:w-[360px] bg-white shadow-2xl rounded-2xl border border-gray-200 overflow-hidden"
    >
      <div className="flex items-center gap-3 p-3">
        {thumbnailUrl ? (
          <img
            src={thumbnailUrl}
            alt=""
            className="w-12 h-12 rounded-lg object-cover flex-shrink-0"
          />
        ) : (
          <div className="w-12 h-12 rounded-lg bg-gray-200 flex-shrink-0" />
        )}

        <div className="flex flex-col min-w-0 flex-1">
          <div className="flex items-center gap-1.5 text-sm font-medium text-gray-900">
            {status === "success" && (
              <CheckCircleIcon
                style={{ fontSize: 16, color: "#16a34a" }}
              />
            )}
            {status === "error" && (
              <ErrorOutlineIcon
                style={{ fontSize: 16, color: "#dc2626" }}
              />
            )}
            <span className="truncate">{headline}</span>
          </div>
          <div className="text-xs text-gray-500 truncate">{subline}</div>
        </div>

        <div className="flex items-center gap-1">
          {video_id && status === "success" && (
            <button
              type="button"
              onClick={() => navigate(`/videos/${video_id}`)}
              aria-label="Open video"
              className="p-1.5 rounded-full hover:bg-gray-100 text-gray-600"
            >
              <OpenInNewIcon style={{ fontSize: 18 }} />
            </button>
          )}
          {isDone && (
            <button
              type="button"
              onClick={dismiss}
              aria-label="Dismiss"
              className="p-1.5 rounded-full hover:bg-gray-100 text-gray-600"
            >
              <CloseIcon style={{ fontSize: 18 }} />
            </button>
          )}
        </div>
      </div>

      {!isDone && (
        <div className="h-1 w-full bg-gray-100">
          <div
            className="h-full bg-gradient-to-r from-indigo-500 to-purple-500 transition-[width] duration-300"
            style={{ width: `${Math.max(2, percent)}%` }}
          />
        </div>
      )}
    </div>
  );
}
